/**
 * Shopify-style “compare at” (strikethrough) pricing — percentage is never persisted.
 */

import type { VariantRow } from '@/components/merchant/products/variantBuilderTypes'

export type DiscountComputation = {
  amount: number
  pct: number
  rawPct: number
}

/**
 * Single source of truth for discount math (dashboard + storefront).
 * Returns null when there is no valid promotion or inputs are unsafe (NaN, division by zero).
 */
export function computeDiscount(price: number, compareAt: number): DiscountComputation | null {
  if (!Number.isFinite(price) || !Number.isFinite(compareAt)) return null
  if (!price || price <= 0) return null
  if (!compareAt || compareAt <= price) return null

  const rawPct = (1 - price / compareAt) * 100
  if (!Number.isFinite(rawPct)) return null

  const pct = Math.round(rawPct)
  const amount = compareAt - price

  return {
    amount,
    pct,
    rawPct,
  }
}

export function validateCompareAt(price: number, compareAt?: number | null): string | null {
  if (!compareAt) return null
  if (!price || price <= 0) return 'Prix invalide'

  if (compareAt <= price) {
    return 'Le prix comparé doit être supérieur au prix de vente'
  }

  return null
}

/** Parse optional number from dashboard text fields (comma as decimal). Non-finite / negative → clamped or null. */
export function parseOptionalPrice(raw: string): number | null {
  const t = raw.trim().replace(',', '.')
  if (t === '') return null
  const n = Number.parseFloat(t)
  if (!Number.isFinite(n)) return null
  return Math.max(0, n)
}

/**
 * Card/list price + compare-at for catalog rows.
 * Variable products: use the cheapest variant’s price and compare (never product-level compare).
 */
export function resolveListCompareAt(product: {
  has_variants?: boolean | null
  price?: number | null
  min_price?: number | null
  compare_at_price?: number | null
  comparePrice?: number | null
  variants?: Array<{ price?: number | null; compare_at_price?: number | null }> | null
}): { price: number; compareAt: number | null } {
  if (product.has_variants && product.variants?.length) {
    const lowest = product.variants.reduce((a, b) =>
      Number(a.price ?? 0) <= Number(b.price ?? 0) ? a : b
    )
    const price = Number(lowest.price ?? product.min_price ?? product.price ?? 0)
    const cap = lowest.compare_at_price != null ? Number(lowest.compare_at_price) : null
    return {
      price,
      compareAt: cap != null && Number.isFinite(cap) ? cap : null,
    }
  }
  const price = Number(product.price ?? 0)
  const raw = product.compare_at_price ?? product.comparePrice
  const cap = raw != null ? Number(raw) : null
  return {
    price,
    compareAt: cap != null && Number.isFinite(cap) ? cap : null,
  }
}

/** First invalid variant row message, or null if all OK (ignores archived rows). */
export function validateVariantRowsCompareAt(rows: VariantRow[]): string | null {
  for (const r of rows) {
    if (r.status === 'archived') continue
    const sale = Number(r.price ?? 0)
    const err = validateCompareAt(sale, r.compare_at_price)
    if (err) return err
  }
  return null
}
